import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCColorPicker, RCCGrid, RCCHidden, RCCInput, RCCRadio, RCCSelectOptionsCard, RCCSwitch, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import Modals from "src/pages/Indicators/Fields/modals";
import InputNumber from "src/components/InputNumber";
import { useWatch } from "react-hook-form";
import Title from "src/components/Title";
import Select from "src/components/Select";
import LovDecimalPlaces from "src/components/Lov/DecimalPlaces";
import { IRefIndicatorFieldModals } from "src/pages/Indicators/Fields/interface";
import { css } from "styled-components";
import returnColor from "src/utils/returnColor";
import areaChart from "src/assets/icons/area-chart.svg";
import chart from "src/assets/icons/chart.svg";
import lineChart from "src/assets/icons/line-chart.svg";
import formatNumber from "src/utils/formatNumber";
import LovPeriods from "src/components/Lov/Periods";
import returnStringValueForSelect from "src/utils/returnStringValueForSelect";
import { AccumulateType, GraphicType, IGoalsTab, IIqtbc002 } from "../interface";
import { colorPickerStyle, FieldAlwaysOnRight, FieldOnRight } from "../styles";

const getSrcChartType = (chartType: string | number) => {
	if(!chartType)
		return lineChart;
	if(GraphicType.AREA === chartType.toString())
		return areaChart;
	if(GraphicType.LINE === chartType.toString())
		return lineChart;
	return chart;
};


const TabGoals: React.FC<IGoalsTab> = ({ 
	goal,
	competitiveness,
	compromise,
	fields,
	register,
	control,
	errors,
	selectedIndicator,
	setValue,
	hidden,
	isAdd,
	orderGoals
}) => {
	if(hidden) return null;
	
	const { translate,currentLanguage} = useTranslation();
	const [localFields,setLocalFields] = useState<Array<{value:string,label:string}>>(fields || []);
	const [onChangeGoal,setOnChangeGoal] = useState<"compromise"|"goal"|"competitiveness">("goal");
	const refModals = useRef<IRefIndicatorFieldModals>(null);
	
	const fieldsWatch = useWatch({
		control,
		name: "fields"
	});

	useEffect(() => {
		if(fieldsWatch)
			setLocalFields(() => {
				const  temporaryFields = [] as Array<{value:string,label:string}>;
				fieldsWatch.forEach((field:IIqtbc002) => 
				{
					if(field.iqc002Iqb001Id){
						temporaryFields.push({value:returnStringValueForSelect(field.iqc002Iqb001Id),label:field.description||""});
					} 
				});
				return temporaryFields;
			});
	}, [fieldsWatch]);

	const [watchUseCompromise, setWatchUseCompromise] = useState(Boolean(compromise)); 

	const [watchUseCompetitiveness, setWatchUseCompetitiveness] = useState(Boolean(competitiveness));

	const watchDecimalPlaces = useWatch({
		control,
		name: "goal.decimalPlaces",
		defaultValue: goal?.decimalPlaces||2
	});

	const watchPeriodType = useWatch({
		control,
		name: "indicator.iqc001Iqb003Id"
	});

	const [watchGraphicType, setWatchGraphicType] = useState(goal?.iqc002Tipografico?.toString()||GraphicType.LINE);
	const [watchChartAccumulatedType, setWatchChartAccumulatedType] = useState(goal?.iqc002Tpgrafacum?.toString()||GraphicType.LINE);

	const goalField = (goalItem:IIqtbc002,objectName:"compromise"|"goal"|"competitiveness") => { 
		const [usebaseFields, setUseBaseFields] = useState(!!goalItem?.iqc002Utilizarcamposbase);
		const [showGraphics, setShowGraphics] = useState(goalItem?.iqc002Mostragrafico === undefined || !!goalItem?.iqc002Mostragrafico);
		const [fieldColor,setFieldColor]= useState(goalItem?.iqc002CorGrafico && !goalItem?.iqc002CorGrafico?.startsWith("#") ? `#${goalItem.iqc002CorGrafico}` :"#ffffff0");
		const [showUpdateGoalValues,setShowUpdateGoalValues] = useState(false);
		const [showPeriodsLov,setShowPeriodsLov] = useState(false);

		let labelDefault;
		switch (objectName) {
		case "compromise":
			labelDefault = translate("Meta compromisso");
			break;
		case "competitiveness":
			labelDefault = translate("Meta competitividade");
			break;
		default:
			labelDefault = translate("Meta melhoria");
			break;
		}	

		return(
			<RCCGrid container item>
				<RCCGrid container item
					xs={6}
				>		
					<RCCInput 
						name={`${objectName}.label`}
						register={register}
						label={translate("Rótulo")}
						defaultValue={goalItem?.label || labelDefault}
						error={errors[objectName]?.label}
						maxLength={60}
						required
					/>
					<input 
						{...register(`${objectName}.iqc002Id`)} 
						name={`${objectName}.iqc002Id`}
						value={goalItem?.iqc002Id||0}
						type="hidden"
					/>
					<input 
						{...register(`${objectName}.iqc002Iqb001Id`)} 
						name={`${objectName}.iqc002Iqb001Id`}
						value={goalItem?.iqc002Iqb001Id||0}
						type="hidden"
					/>
				</RCCGrid>
				<RCCGrid container item
					xs={6}
					styleInline={FieldAlwaysOnRight}
					wrap="nowrap"
					alignItems="center"
				>		
					<InputNumber 
						name={`${objectName}.iqc002Metadefault`}
						control={control}
						allowNegative
						decimalScale={watchDecimalPlaces||2}
						label={translate("Meta padrão")}
						defaultValue={goalItem?.iqc002Metadefault || 0}
						error={errors[objectName]?.iqc002Metadefault}
						maxLength={14}
						required
						onBlur={(e) => {
							setShowUpdateGoalValues(e?.target?.value !== formatNumber(goalItem?.iqc002Metadefault || 0,currentLanguage,watchDecimalPlaces||2));
						}}
					/>
					{ (goalItem?.iqc002Id && goalItem.iqc002Id !== 0) &&
						<>
							<RCCTooltip
								text={translate("Alterar meta")}
							>
								<RCCButton
									icon={{name:"pencil"}}
									size="sm"
									styleInline="margin-bottom:-15px;"
									onClick={
										() => {
											setOnChangeGoal(objectName);
											refModals.current?.actionButtonChange({iqb001Id:parseInt(goalItem?.iqc002Iqb001Id||"",10),iqb001Codigo:"",iqb001Descricao:"",iqb001Label:""}, undefined, selectedIndicator);
										}
									}
								/>
							</RCCTooltip>
						</>
					}
				</RCCGrid>
				{(goalItem?.iqc002Id && goalItem.iqc002Id !== 0) && !isAdd && showUpdateGoalValues &&
					<RCCGrid 
						container 
						item
						xs={12}
					>
						<RCCAlert
							styleInline={css`
								&:hover{
									filter:unset;
								}`
							}
							description={
								<>
									{translate("Você alterou o valor padrão para as metas. Deseja alterar a meta dos indicadores?")}
									<RCCRadio
										register={register}
										name={`${objectName}.updateOldValues`}
										label={translate("")}
										defaultValue="N"
										color="purple"
										direction="vertical" 
										options={[
											{value:"N",label:translate("Apenas para os próximos")},
											{value:"S",label:translate("Alterar valores do passado")}
										]}
										onChangeCapture={(e: any) => setShowPeriodsLov(String(e.target.value) === "S")}
									/>
									{showPeriodsLov &&
										<LovPeriods
											key={watchPeriodType}
											name={`${objectName}.startPeriodUpdate`}
											label={translate("Periodo inicial")}
											control={control}
											error={errors[objectName]?.startPeriodUpdate}
											extraParametersOnLoad={{
												periodTypeId:watchPeriodType
											}}
										/>
									}
								</>
							}
						/>
					</RCCGrid>
				}
				<RCCGrid container item
					xs={12}
				>	
					<RCCGrid container item xs={6}>
						<RCCSwitch
							name={`${objectName}.iqc002Utilizarcamposbase`}
							register={register}
							label={translate("Utilizar Campos Base")}
							defaultChecked={goalItem?.iqc002Utilizarcamposbase}
							labelPosition="end"
							onChangeCapture={(e:any) => setUseBaseFields(e.target.checked)}
						/>
					</RCCGrid>	

					<RCCGrid container item xs={6} styleInline={FieldOnRight}>
						<RCCSwitch
							name={`${objectName}.iqc002Mostragrafico`}
							register={register}
							label={translate("Mostrar gráfico")}
							defaultChecked={showGraphics}
							labelPosition="end"
							onChangeCapture={(e:any) => setShowGraphics(e.target.checked)}
						/>
					</RCCGrid>	
					<RCCHidden xsUp={!showGraphics}>
						<RCCGrid  container item>
							<RCCGrid container item xs={6}>
								<RCCSwitch
									name={`${objectName}.iqc002Mostrarvalor`}
									register={register}
									label={translate("Mostrar valor")}
									defaultChecked={goalItem?.iqc002Mostrarvalor}
									labelPosition="end"
								/>
							</RCCGrid>
							<RCCGrid container item xs={6} styleInline={FieldOnRight} alignItems="center">
								<RCCColorPicker
									name={`${objectName}.iqc002CorGrafico`}
									control={control}
									label={translate("Cor")}
									labelPosition="start"
									colorPickerProps ={{color:returnColor(fieldColor||"#ffffff0")}}
									defaultValue = {returnColor(fieldColor)}
									styleInline={colorPickerStyle}
									onColorChange={(e) => {
										setValue(`${objectName}.iqc002CorGrafico`,e); 
										setFieldColor(e);
									}}
								/>
								{fieldColor && (
									<RCCTooltip
										text={translate("Limpar cor")}
										color="yellow"	
									>
										<RCCButton 
											onClick={() => { 
												setValue(`${objectName}.iqc002CorGrafico`,""); 
												setFieldColor("");
											}}
											size="xs" 
											color="transparent" 
											icon={{
												name:"broom",
												size:14,
												color: "var(--yellow)"
											}} 
											layout="isRounded"
										/>
									</RCCTooltip>
								)}
							</RCCGrid>
						</RCCGrid>
					</RCCHidden>
				</RCCGrid>
				<RCCHidden xsUp={!usebaseFields}>
					<RCCGrid container item >
						<RCCGrid container item 
							sm={6}
						> 
							<Select
								name={`${objectName}.iqc002Dividendo`}
								label={translate("Dividendo")}
								control={control}
								options={localFields}
								error={errors[objectName]?.iqc002Dividendo}
								required
								defaultValue={returnStringValueForSelect(goalItem?.iqc002Dividendo)}
							/>
						</RCCGrid>		
						<RCCGrid container item 
							styleInline={FieldOnRight}
							sm={6}
						> 
							<Select
								name={`${objectName}.iqc002Divisor`}
								label={translate("Divisor")}
								control={control}
								options={localFields}
								error={errors[objectName]?.iqc002Divisor}
								required
								defaultValue={returnStringValueForSelect(goalItem?.iqc002Divisor)}
							/>
						</RCCGrid>
					</RCCGrid>
				</RCCHidden>
			</RCCGrid>
		);
	};
	return(
		<RCCGrid container item 
			justifyContent="center"
		>
			<RCCGrid container item 
				xs={12} 
				sm={10} 
				md={8}
			>
				<RCCGrid container>
					<Title 
						size="md" 
						color="purple" 
						marginTop={10}
					>
						{translate("Meta melhoria")}
					</Title>
					{goalField(goal,"goal")}
					<hr/>
				</RCCGrid>
				<RCCGrid container>
					<Title 
						size="md" 
						color="purple" 
						marginTop={10}
					>
						{translate("Competitividade")}
					</Title>
					<RCCGrid container>
						<Title 
							size="xs"
							color="grey"
						>
							{translate("Deve ser usado como valor referencial comparativo, pode ser um concorrente ou a média do setor em que a empresa atua")}
						</Title>
					</RCCGrid>
					<RCCGrid container>
						<RCCSwitch
							name="useCompetitiveness"
							register={register}
							label={translate("Usar meta competitividade")}
							defaultChecked={watchUseCompetitiveness}
							labelPosition="end"
							onChangeCapture={(e: any) => setWatchUseCompetitiveness(e.target.checked)}
						/>
					</RCCGrid>
					<RCCHidden xsUp={!watchUseCompetitiveness}>		
						{goalField(competitiveness||{} as IIqtbc002,"competitiveness")}
					</RCCHidden>
					<hr/>
				</RCCGrid>
				<RCCGrid container>
					<Title 
						size="md" 
						color="purple" 
						marginTop={10}
					>
						{translate("Compromisso")}
					</Title>
					<RCCGrid container>
						<Title 
							size="xs"
							color="grey"
						>
							{translate("Deve ser usado como valor referencial pelas partes interessadas")}: {` ${translate("clientes")}, ${translate("colaboradores")}, ${translate("sócios")}. ${translate("Colocar a meta que o sócio quer, por exemplo, 25% de lucro para o próximo ano")}`}
						</Title>
					</RCCGrid>
					<RCCGrid container>
						<RCCSwitch
							name="useCompromise"
							register={register}
							label={translate("Usar meta compromisso")}
							defaultChecked={watchUseCompromise}
							labelPosition="end"
							onChangeCapture={(e: any) => setWatchUseCompromise(e.target.checked)}
						/>
					</RCCGrid>
					<RCCHidden xsUp={!watchUseCompromise}>		
						{goalField(compromise ||{} as IIqtbc002,"compromise")}
					</RCCHidden>
					<hr/>
				</RCCGrid>
				<RCCGrid container>
					<Title 
						size="md" 
						color="purple" 
						marginTop={10}
					>
						{translate("Visual nos gráficos")}
					</Title>
					<RCCGrid container
						wrap="nowrap"
					>
						<RCCGrid container item>
							<LovDecimalPlaces
								control={control}
								name="goal.decimalPlaces"
								required
								defaultValue={goal?.decimalPlaces?.toString()}
								error={errors?.goal?.decimalPlaces}
							/>
						</RCCGrid>
						<RCCGrid container item styleInline={FieldAlwaysOnRight}>
							<Select
								control={control}
								name="goal.iqc002Tipoacum"
								label={translate("Tipo de acumulador")}
								defaultValue={goal?.iqc002Tipoacum||AccumulateType.AVERAGE}
								error={errors?.goal?.iqc002Tipoacum}
								options={[
									{value:AccumulateType.AVERAGE,label:translate("Média")},
									{value:AccumulateType.LAST_VALUE,label:translate("Ultimo valor")},
									{value:AccumulateType.SUM,label:translate("Soma")},
									{value:AccumulateType.FORMULA,label:translate("Fórmula")}
								]}
							/>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid container 
						wrap="nowrap"
					>
						<RCCGrid container item>						
							<RCCRadio
								register={register}
								name="goal.iqc002Tipografico"
								label={translate("Tipo de gráfico")}
								defaultValue={goal?.iqc002Tipografico?.toString()||GraphicType.LINE}
								color="purple"
								direction="vertical" 
								options={[
									{value:GraphicType.AREA,label:translate("Area")},
									{value:GraphicType.COLUMN,label:translate("Coluna")},
									{value:GraphicType.LINE,label:translate("Linha")}
								]}
								onChangeCapture={(e: any) => setWatchGraphicType(String(e.target.value))}
							/>
							<img 	
								src={getSrcChartType(watchGraphicType)} 
								alt="Chart" 
								style={{width:"4rem", marginLeft:"0.5rem"}}
							/>
						
						</RCCGrid>
						<RCCGrid container item styleInline={FieldAlwaysOnRight}>
							<RCCRadio
								register={register}
								name="goal.iqc002Tpgrafacum"
								label={translate("Tipo de gráfico acumulador")}
								defaultValue={goal?.iqc002Tpgrafacum?.toString()||GraphicType.LINE}
								color="purple"
								direction="vertical" 
								options={[
									{value:GraphicType.AREA,label:translate("Area")},
									{value:GraphicType.COLUMN,label:translate("Coluna")},
									{value:GraphicType.LINE,label:translate("Linha")}
								]}
								onChangeCapture={(e: any) => setWatchChartAccumulatedType(String(e.target.value))}
							/>
							<img 	
								src={getSrcChartType(watchChartAccumulatedType)} 
								alt="Chart" 
								style={{width:"4rem", marginLeft:"0.5rem"}}
							/>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid container item>
						<RCCGrid container item sm={6}>
							<RCCSwitch
								name="goal.iqc002Mostrartabela"
								register={register}
								label={translate("Mostrar em tabela")}
								defaultChecked={goal?.iqc002Mostrartabela}
								labelPosition="end"
							/>
						</RCCGrid>
						<RCCGrid container item sm={6} styleInline={FieldAlwaysOnRight}>
							<RCCSelectOptionsCard 
								name="indicator.iqc001Ordemmetas"
								control={control}
								defaultValue={orderGoals || "1"}
								options={[
									{
										value: "2",
										label:translate("Início")
									},
									{
										value: "1",
										label:translate("Fim")
									}
								]}
								label={translate("Ordenar as metas")}
							/>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
			<Modals ref={refModals} onClose={(newField) => setValue(`${onChangeGoal}.label`,newField.iqb001Label)}/>
		</RCCGrid>
	);
};
export default TabGoals;
